import _cloneDeep from "lodash/cloneDeep";
import CryptoJS from "crypto-js";
import { CONSENT_DATA_STORAGE_NAME, NOTIFICATION_STORAGE_NAME } from "../../../constants/env";
import { StorageName, StorageType } from "../../../constants/sdk";
import LoggingModule from "../../logging-module";
import BaseStorageWrapper, { DATA_TEMPLATE } from "./BaseStorageWrapper";
class WebStorageWrapper extends BaseStorageWrapper {
  constructor(name = "WebStorageWrapper", storageName = StorageType.sessionStorage) {
    super(name, false);
    this._decrypt = (encryptedStr, deviceId, userId) => {
      LoggingModule.info(`[${this.name}]: _decrypt`);
      let data = null;
      if (deviceId || userId) {
        const key = `${deviceId != null ? deviceId : ""}_${userId != null ? userId : ""}`;
        try {
          const decryptedBytes = CryptoJS.AES.decrypt(encryptedStr, key);
          const decryptedString = decryptedBytes.toString(CryptoJS.enc.Utf8);
          const parsedData = JSON.parse(decryptedString);
          if ((parsedData == null ? void 0 : parsedData.data) && typeof (parsedData == null ? void 0 : parsedData.timestamp) === "number") {
            data = parsedData;
          }
        } catch (error) {
          LoggingModule.info(`[${this.name}]: _decrypt - ${error.message}`, error);
        }
      }
      LoggingModule.info(`[${this.name}]: _decrypt - finish`, data);
      return data;
    };
    this._encrypt = (data, deviceId, userId) => {
      LoggingModule.info(`[${this.name}]: _encrypt`);
      let str = "";
      if (deviceId || userId) {
        const key = `${deviceId != null ? deviceId : ""}_${userId != null ? userId : ""}`;
        str = CryptoJS.AES.encrypt(JSON.stringify(data), key).toString();
      }
      return str;
    };
    this._encryptConsentStorage = (store) => {
      var _a, _b, _c, _d;
      return {
        consentEntityValidationMap: store.consentEntityValidationMap,
        [StorageName.BOTH_CONSENT]: this._encrypt(store[StorageName.BOTH_CONSENT], (_a = this._did) != null ? _a : "", (_b = this._uid) != null ? _b : ""),
        [StorageName.DEVICE_CONSENT]: this._encrypt(store[StorageName.DEVICE_CONSENT], (_c = this._did) != null ? _c : "", ""),
        [StorageName.USER_CONSENT]: this._encrypt(store[StorageName.USER_CONSENT], "", (_d = this._uid) != null ? _d : "")
      };
    };
    this._updateUserInfo = (deviceId, userId) => {
      this._did = deviceId;
      this._uid = userId;
    };
    this._setConsentWebStorage = (consentStore) => {
      const storageDataWithEncryption = this._encryptConsentStorage(consentStore);
      window[this._storageName].setItem(CONSENT_DATA_STORAGE_NAME, JSON.stringify(storageDataWithEncryption));
    };
    this.insertConsentData = (data, timestamp, userInfo) => {
      LoggingModule.info(`[${this.name}]: insertConsentData`, {
        data,
        timestamp,
        userInfo
      });
      try {
        const store = this.insertDataIntoStore(data, timestamp, userInfo);
        this._setConsentWebStorage(store);
      } catch (error) {
        LoggingModule.error(`[${this.name}]: insertConsentData - ${error.message}`, error);
        throw error;
      }
    };
    this.insertNotificationData = (data, timestamp, userInfo) => {
      LoggingModule.info(`[${this.name}]: insertNotificationData`, {
        data,
        timestamp,
        userInfo
      });
      try {
        const store = this.getNotificationStore(userInfo);
        this.insertDataByStorageName(store, StorageName.NOTIFICATION, data, timestamp);
        window[this._storageName].setItem(NOTIFICATION_STORAGE_NAME, JSON.stringify(store));
      } catch (error) {
        LoggingModule.error(`[${this.name}]: insertNotificationData - ${error.message}`, error);
        throw error;
      }
    };
    this.getConsentStore = (userInfo) => {
      var _a, _b;
      const { device_id: deviceId = "", user_id: userId = "" } = userInfo != null ? userInfo : {};
      let consentEntityValidationMap = {};
      let deviceConsentData = _cloneDeep(DATA_TEMPLATE);
      let userConsentData = _cloneDeep(DATA_TEMPLATE);
      let bothRequiredConsentData = _cloneDeep(DATA_TEMPLATE);
      try {
        const currentStr = (_a = window[this._storageName].getItem(CONSENT_DATA_STORAGE_NAME)) != null ? _a : "{}";
        const storeWithEncryptionData = (_b = JSON.parse(currentStr)) != null ? _b : {};
        consentEntityValidationMap = storeWithEncryptionData.consentEntityValidationMap;
        bothRequiredConsentData = this._retrieveConsentData(
          storeWithEncryptionData,
          StorageName.BOTH_CONSENT,
          deviceId,
          userId
        );
        userConsentData = this._retrieveConsentData(storeWithEncryptionData, StorageName.USER_CONSENT, "", userId);
        deviceConsentData = this._retrieveConsentData(storeWithEncryptionData, StorageName.DEVICE_CONSENT, deviceId, "");
        this._updateUserInfo(deviceId, userId);
      } catch (error) {
        LoggingModule.error(`[${this.name}]: getConsentStore - ${error.message}`, error);
      }
      const newStoreData = {
        consentEntityValidationMap,
        [StorageName.DEVICE_CONSENT]: deviceConsentData,
        [StorageName.USER_CONSENT]: userConsentData,
        [StorageName.BOTH_CONSENT]: bothRequiredConsentData
      };
      this._setConsentWebStorage(newStoreData);
      return newStoreData;
    };
    this.getNotificationStore = (userInfo) => {
      var _a, _b;
      let notification = _cloneDeep(DATA_TEMPLATE);
      try {
        const currentStr = (_a = window[this._storageName].getItem(NOTIFICATION_STORAGE_NAME)) != null ? _a : "{}";
        notification = (_b = JSON.parse(currentStr)) != null ? _b : {};
      } catch (error) {
        LoggingModule.error(`[${this.name}]: getNotificationStore - ${error.message}`, error);
      }
      return {
        [StorageName.NOTIFICATION]: notification
      };
    };
    this.cleanConsentData = () => {
      window[this._storageName].removeItem(CONSENT_DATA_STORAGE_NAME);
    };
    this.cleanNotificationData = () => {
      window[this._storageName].removeItem(NOTIFICATION_STORAGE_NAME);
    };
    this.clean = () => {
      window[this._storageName].removeItem(CONSENT_DATA_STORAGE_NAME);
      window[this._storageName].removeItem(NOTIFICATION_STORAGE_NAME);
    };
    this.destruct = () => {
      this.clean();
    };
    this._storageName = storageName;
  }
  _retrieveConsentData(storeWithEncryptionData, storageName, deviceId, userId) {
    let isValid = false;
    const defaultData = _cloneDeep(DATA_TEMPLATE);
    switch (storageName) {
      case StorageName.BOTH_CONSENT: {
        isValid = deviceId === this._did && userId === this._uid;
        break;
      }
      case StorageName.USER_CONSENT: {
        isValid = userId === this._uid;
        break;
      }
      case StorageName.DEVICE_CONSENT: {
        isValid = deviceId === this._did;
        break;
      }
      default: {
        break;
      }
    }
    if (isValid) {
      const data = this._decrypt(storeWithEncryptionData[storageName], deviceId, userId);
      if (data) {
        return data;
      }
    }
    return defaultData;
  }
}
class LocalStorageWrapper extends WebStorageWrapper {
  constructor() {
    super("LocalStorageWrapper", StorageType.localStorage);
  }
}
class SessionStorageWrapper extends WebStorageWrapper {
  constructor() {
    super("SessionStorageWrapper", StorageType.sessionStorage);
  }
}
export {
  LocalStorageWrapper,
  SessionStorageWrapper
};
